import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="sales-form"
export default class extends Controller {
    static targets = [
    "lineItemsContainer", "lineItem", "productSelect", "quantity", "unitPrice", "lineTotal",
    "productDetails", "productSku", "minQuantity", "maxQuantity", "pricingTiers", "pricingTiersList", "alerts",
    "subtotal", "discountAmount", "taxAmount", "total", "totalAmountField",
    "saveDraftBtn", "submitBtn", "loadingOverlay", "destroyField",
    "customerName", "customerEmail", "customerPhone", "customerAddress", "customerCity", "customerState", "customerZip",
    "validationError", "form",
    "productCode", "productName", "productCodeDisplay",
    "installationCredit", "shippingAmount", "paymentCash", "paymentCheck", "paymentCreditCard", "checkNumber",
    "customPrice", "discountReason", "discountReasonRow",
    "installationField", "installationPhotosField", "includeInstallation"
  ]

  static values = {
    products: Array,
    preselectedProductId: String
  }

  // Ensure an option exists on a select; adds if missing
  ensureOption(selectEl, value, label) {
    if (!value && value !== 0) return
    const exists = Array.from(selectEl.options).some(o => o.value == value)
    if (!exists) {
      const opt = document.createElement('option')
      opt.value = value
      opt.textContent = label
      // Insert after placeholder
      if (selectEl.options.length > 0) {
        selectEl.add(opt, selectEl.options[1] || null)
      } else {
        selectEl.add(opt)
      }
    }
  }

  // Populate code/product/price selects with full catalog (for explicit 3-step confirmation)
  populateCatalogOptions(lineItem) {
    const codeSelect = lineItem.querySelector('[data-sales-form-target="productCodeDisplay"]')
    const nameSelect = null
    const priceSelect = lineItem.querySelector('[data-sales-form-target="unitPriceDisplay"]') || lineItem.querySelector('[data-sales-form-target="unitPrice"]')
    if (codeSelect && codeSelect.tagName === 'SELECT') {
      // keep placeholder at index 0
      this.productsValue.forEach(p => this.ensureOption(codeSelect, p.sku, p.sku))
    }
    // no second product selection dropdown
    if (priceSelect && priceSelect.tagName === 'SELECT') {
      // Provide a reasonable set of price choices (MSRP + tier bases if present)
      const addPrice = (n) => this.ensureOption(priceSelect, Number(n).toFixed(2), `$${Number(n).toFixed(2)}`)
      this.productsValue.forEach(p => {
        if (p.price) addPrice(p.price)
        if (Array.isArray(p.pricing_tiers)) {
          p.pricing_tiers.forEach(t => {
            if (t.base_price) addPrice(t.base_price)
            if (t.retail_price) addPrice(t.retail_price)
            if (t.wholesale_price) addPrice(t.wholesale_price)
          })
        }
      })
    }
  }

  // Coerce any value to a finite number, otherwise default to 0
  toNumber(value) {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : 0
  }

  connect() {
    console.log("Sales form controller connected")
    this.lineItemIndex = this.lineItemTargets.length
    this.validationErrors = new Map()
    this.updateTotals()

    // Initialize existing line items
    this.lineItemTargets.forEach(lineItem => {
      this.initializeLineItem(lineItem)
    })

    // Auto-add line item with preselected product if provided
    if (this.hasPreselectedProductIdValue && this.preselectedProductIdValue && this.lineItemTargets.length === 0) {
      this.addLineItem()
      const newItem = this.lineItemsContainerTarget.lastElementChild
      const productSelect = newItem.querySelector('[data-sales-form-target="productSelect"]')
      if (productSelect) {
        productSelect.value = this.preselectedProductIdValue
        // Trigger change event to populate product details
        productSelect.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }

    // Set up real-time validation
    this.setupValidation()

    // Bind form submission
    if (this.hasFormTarget) {
      this.formTarget.addEventListener('submit', this.handleFormSubmit.bind(this))
    }

    // Set up beforeUnload warning
    this.boundBeforeUnload = this.beforeUnload.bind(this)
    window.addEventListener('beforeunload', this.boundBeforeUnload)

    // Set up keyboard shortcuts
    this.boundKeyHandler = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundKeyHandler)

    // Add select-all behavior to all number inputs on the form
    this.addSelectAllBehaviorToNumberInputs()
  }
  
  // Add select-all-on-focus behavior to number inputs throughout the form
  addSelectAllBehaviorToNumberInputs() {
    // Find all number inputs in the form
    const numberInputs = this.element.querySelectorAll('input[type="number"], input[step]')
    
    numberInputs.forEach(input => {
      // Remove existing listeners to avoid duplicates
      input.removeEventListener('focus', this.selectAllOnFocus)
      input.removeEventListener('click', this.selectAllOnFocus)
      
      // Add the select-all behavior
      input.addEventListener('focus', this.selectAllOnFocus.bind(this))
      input.addEventListener('click', this.selectAllOnFocus.bind(this))
    })
  }

  disconnect() {
    // Remove event listeners
    if (this.boundBeforeUnload) {
      window.removeEventListener('beforeunload', this.boundBeforeUnload)
    }

    if (this.boundKeyHandler) {
      document.removeEventListener('keydown', this.boundKeyHandler)
    }
  }

  // Handle keyboard shortcuts
  handleKeydown(event) {
    // Ctrl+S or Cmd+S to save draft
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault()
      this.saveDraft()
    }
  }

  // Set up real-time validation for form fields
  setupValidation() {
    // Customer fields validation
    if (this.hasCustomerNameTarget) {
      this.customerNameTarget.addEventListener('blur', () => this.validateCustomerName())
      this.customerNameTarget.addEventListener('input', () => this.clearFieldError(this.customerNameTarget))
    }

    if (this.hasCustomerEmailTarget) {
      this.customerEmailTarget.addEventListener('blur', () => this.validateCustomerEmail())
      this.customerEmailTarget.addEventListener('input', () => this.clearFieldError(this.customerEmailTarget))
    }

    if (this.hasCustomerPhoneTarget) {
      this.customerPhoneTarget.addEventListener('blur', () => this.validateCustomerPhone())
      this.customerPhoneTarget.addEventListener('input', () => this.clearFieldError(this.customerPhoneTarget))
    }

    // Discount and tax validation
    if (this.hasDiscountAmountTarget) {
      this.discountAmountTarget.addEventListener('blur', () => this.validateDiscountAmount())
    }

    if (this.hasTaxAmountTarget) {
      this.taxAmountTarget.addEventListener('blur', () => this.validateTaxAmount())
    }
  }

  // Initialize a line item with current values
  initializeLineItem(lineItem) {
    const productSelect = lineItem.querySelector('[data-sales-form-target="productSelect"]')
    const quantity = lineItem.querySelector('[data-sales-form-target="quantity"]')
    const customPriceField = lineItem.querySelector('[data-sales-form-target="customPrice"]')
    
    // Pre-populate triple-check selects with full catalog
    this.populateCatalogOptions(lineItem)

    // Set up event listeners for custom price field (admin/manager only)
    if (customPriceField) {
      customPriceField.addEventListener('input', this.customPriceChanged.bind(this))
      customPriceField.addEventListener('change', this.customPriceChanged.bind(this))
      
      // Check if this line item already has a custom price set
      if (customPriceField.value && parseFloat(customPriceField.value) > 0) {
        // Show discount reason row
        const discountReasonRow = lineItem.nextElementSibling
        if (discountReasonRow && discountReasonRow.dataset.salesFormTarget === 'discountReasonRow') {
          discountReasonRow.classList.remove('hidden')
        }
      }
    }

    if (productSelect && productSelect.value && quantity && quantity.value) {
      // Check if this line item already has a saved unit price (existing record)
      const unitPriceInput = lineItem.querySelector('[data-sales-form-target="unitPriceDisplay"]') || lineItem.querySelector('[data-sales-form-target="unitPrice"]')
      const hasExistingPrice = unitPriceInput && unitPriceInput.value && unitPriceInput.value !== ''
      
      if (hasExistingPrice) {
        // For existing records, preserve the saved price and just update totals
        console.log('Preserving existing price:', unitPriceInput.value)
        this.updateTotals()
      } else {
        // For new records, calculate the price
        this.updateLineItemPriceForElement(lineItem)
      }
    }
  }

  // Add a new line item
  addLineItem() {
    const template = this.createLineItemTemplate()
    this.lineItemsContainerTarget.insertAdjacentHTML('beforeend', template)
    const newItem = this.lineItemsContainerTarget.lastElementChild
    this.initializeLineItem(newItem)
    
    // Add select-all behavior to number inputs in the new line item
    const numberInputs = newItem.querySelectorAll('input[type="number"], input[step]')
    numberInputs.forEach(input => {
      input.addEventListener('focus', this.selectAllOnFocus.bind(this))
      input.addEventListener('click', this.selectAllOnFocus.bind(this))
    })
    
    this.updateTotals()
  }

  // Remove a line item
  removeLineItem(event) {
    const lineItem = event.target.closest('[data-sales-form-target="lineItem"]')
    const destroyField = lineItem.querySelector('[data-sales-form-target="destroyField"]')

    if (destroyField && destroyField.value !== "") {
      // This is an existing record, mark for destruction
      destroyField.value = "1"
      lineItem.classList.add("line-item-removing")
    } else {
      // This is a new record, remove from DOM
      lineItem.remove()
    }

    this.updateTotals()
  }

  // Update line item price when product or quantity changes
  updateLineItemPrice(event) {
    const lineItem = event.target.closest('[data-sales-form-target="lineItem"]')
    console.log('updateLineItemPrice triggered by:', event.target.name || event.target.dataset.salesFormTarget, 'value:', event.target.value)
    
    // If this is a quantity change and we already have a product selected,
    // we might need to handle it differently than a full product change
    if (event.target.dataset.salesFormTarget === 'quantity') {
      this.handleQuantityChange(lineItem)
    } else {
      this.updateLineItemPriceForElement(lineItem)
    }
  }

  // Handle quantity changes specifically
  handleQuantityChange(lineItem) {
    const productSelect = lineItem.querySelector('[data-sales-form-target="productSelect"]')
    const quantityInput = lineItem.querySelector('[data-sales-form-target="quantity"]')
    const unitPriceInput = lineItem.querySelector('[data-sales-form-target="unitPriceDisplay"]') || lineItem.querySelector('[data-sales-form-target="unitPrice"]')
    const lineTotalInput = lineItem.querySelector('[data-sales-form-target="lineTotal"]')
    
    const quantity = parseInt(quantityInput.value) || 0
    
    // If we don't have a product selected, clear everything
    if (!productSelect || !productSelect.value) {
      this.clearLineItemDetails(lineItem)
      return
    }
    
    // If we have a unit price already (from previous selection), just update the line total
    if (unitPriceInput && unitPriceInput.value) {
      let unitPrice = 0
      
      // Handle both input and select unit price fields
      if (unitPriceInput.tagName === 'SELECT') {
        unitPrice = parseFloat(unitPriceInput.value) || 0
      } else {
        unitPrice = parseFloat(unitPriceInput.value) || 0
      }
      
      const lineTotal = quantity * unitPrice
      
      console.log('handleQuantityChange: quantity=', quantity, 'unitPrice=', unitPrice, 'lineTotal=', lineTotal)
      
      if (lineTotalInput) {
        lineTotalInput.value = lineTotal.toFixed(2)
      }
      
      // Update the hidden unit_price field if it exists (for dynamically created items)
      const hiddenUnitPrice = lineItem.querySelector('input[name*="[unit_price]"]')
      if (hiddenUnitPrice) {
        hiddenUnitPrice.value = unitPrice.toFixed(2)
        console.log('  - updated hidden unit price to:', hiddenUnitPrice.value)
      }
      
      // For existing items, make sure the select element's value is preserved
      if (unitPriceInput && unitPriceInput.tagName === 'SELECT' && unitPriceInput.value != unitPrice.toFixed(2)) {
        // Only update if it's different to avoid infinite loops
        unitPriceInput.value = unitPrice.toFixed(2)
        console.log('  - updated select unit price to:', unitPriceInput.value)
      }
      
      // Also update the hidden total_price field that Rails uses
      const hiddenTotalPrice = lineItem.querySelector('input[name*="[total_price]"]')
      if (hiddenTotalPrice) {
        hiddenTotalPrice.value = lineTotal.toFixed(2)
      }
      
      this.updateTotals()
    } else {
      // No unit price yet, need to do full price calculation
      this.updateLineItemPriceForElement(lineItem)
    }
  }

  // Update price for a specific line item element - Triple-Check System
  async updateLineItemPriceForElement(lineItem) {
    const productSelect = lineItem.querySelector('[data-sales-form-target="productSelect"]')
    const quantityInput = lineItem.querySelector('[data-sales-form-target="quantity"]')
    const unitPriceInput = lineItem.querySelector('[data-sales-form-target="unitPriceDisplay"]') || lineItem.querySelector('[data-sales-form-target="unitPrice"]')
    const lineTotalInput = lineItem.querySelector('[data-sales-form-target="lineTotal"]')
    const productDetails = lineItem.querySelector('[data-sales-form-target="productDetails"]')
    const alerts = lineItem.querySelector('[data-sales-form-target="alerts"]')

    // Triple-check system fields (hidden form fields and display fields)
    const productCodeInput = lineItem.querySelector('[data-sales-form-target="productCode"]')
    const productNameInput = lineItem.querySelector('[data-sales-form-target="productName"]')
    const productCodeDisplay = lineItem.querySelector('[data-sales-form-target="productCodeDisplay"]')

    if (!productSelect || !quantityInput || !unitPriceInput || !lineTotalInput) return

    const productId = parseInt(productSelect.value)
    const quantity = parseInt(quantityInput.value) || 1

    if (!productId) {
      this.clearLineItemDetails(lineItem)
      return
    }

    const product = this.productsValue.find(p => p.id === productId)
    if (!product) {
      this.clearLineItemDetails(lineItem)
      return
    }

    // Populate choices; do not auto-select. Require explicit confirmation.
    const productSku = product.sku || ''
    const productName = product.name || ''

    if (productCodeDisplay) {
      if (productCodeDisplay.tagName === 'SELECT') {
        this.ensureOption(productCodeDisplay, productSku, productSku)
        productCodeDisplay.dataset.expected = productSku
        if (productCodeDisplay.value !== productSku) {
          productCodeDisplay.value = ''
        }
      } else {
        productCodeDisplay.value = ''
        productCodeDisplay.dataset.expected = productSku
      }
    }
    // Product name readout removed
    if (productCodeInput) productCodeInput.value = ''
    if (productNameInput) productNameInput.value = ''

    // Show loading state without breaking <input type="number">
    unitPriceInput.value = ''
    lineTotalInput.value = ''
    unitPriceInput.placeholder = '...'
    lineTotalInput.placeholder = '...'

    try {
      // Skip product details blue box to keep UI to 3 explicit selections (code, price, product)

      // Get installation preference from sales order type
      const includeInstallation = this.getSalesOrderInstallationPreference()

      // Calculate price using API with fallback
      const priceData = await this.calculateProductPrice(product, quantity, includeInstallation)
      const price = this.toNumber(priceData.price)
      const lineTotal = price * quantity

      const expectedPrice = price.toFixed(2)
      if (unitPriceInput.tagName === 'SELECT') {
        this.ensureOption(unitPriceInput, expectedPrice, `$${expectedPrice}`)
        unitPriceInput.dataset.expected = expectedPrice
        unitPriceInput.value = ''
      } else {
        unitPriceInput.value = expectedPrice
        unitPriceInput.dataset.expected = expectedPrice
        this.setMismatchState(unitPriceInput, false)
        unitPriceInput.placeholder = ''
        
        // For input fields, immediately calculate line total
        const lineTotal = price * quantity
        if (lineTotalInput) {
          lineTotalInput.value = lineTotal.toFixed(2)
        }
        
        // Update hidden fields
        const hiddenUnitPrice = lineItem.querySelector('input[name*="[unit_price]"]')
        if (hiddenUnitPrice) {
          hiddenUnitPrice.value = price.toFixed(2)
        }
        const hiddenTotalPrice = lineItem.querySelector('input[name*="[total_price]"]')
        if (hiddenTotalPrice) {
          hiddenTotalPrice.value = lineTotal.toFixed(2)
        }
      }
      
      if (unitPriceInput.tagName === 'SELECT') {
        lineTotalInput.value = ''
        lineTotalInput.placeholder = ''
      }

      // (Optional) warnings disabled for simplified 3-select UI

      // Also compare current user-entered overrides and flag mismatches if any
      if (productCodeDisplay) {
        const mismatchCode = productCodeDisplay.value.trim() !== (productCodeDisplay.dataset.expected || '')
        this.setMismatchState(productCodeDisplay, mismatchCode)
      }
      if (unitPriceInput && unitPriceInput.tagName !== 'SELECT') {
        const mismatchPrice = (parseFloat(unitPriceInput.value || '0') || 0).toFixed(2) !== (unitPriceInput.dataset.expected || '')
        this.setMismatchState(unitPriceInput, mismatchPrice)
      }

      // Update order totals
      this.updateTotals()
    } catch (error) {
      console.error('Error updating line item price:', error)
      
      // Show contextual error to user
      this.showPricingError(error, product, () => this.updateLineItemPrice(lineItem))

      // Fallback to appropriate price based on sales order type
      const includeInstallation = this.getSalesOrderInstallationPreference()
      const fallbackPrice = this.getProductPriceForInstallation(product, includeInstallation)
      const lineTotal = fallbackPrice * quantity

      unitPriceInput.value = fallbackPrice.toFixed(2)
      lineTotalInput.value = lineTotal.toFixed(2)
      unitPriceInput.placeholder = ''
      lineTotalInput.placeholder = ''

      this.showNotification('Error calculating price, using default pricing', 'error')
      this.updateTotals()
    }
  }

  // Handle user selection for product code confirmation
  manualProductCodeSelected(event) {
    const select = event.target
    const expected = select.dataset.expected || ''
    const mismatched = expected && select.value.trim() !== expected
    this.setMismatchState(select, mismatched)
    const lineItem = select.closest('[data-sales-form-target="lineItem"]')
    const hidden = lineItem.querySelector('[data-sales-form-target="productCode"]')
    if (hidden) hidden.value = select.value
  }

  // Handle price selection; compare with expected and recalc totals
  manualUnitPriceSelected(event) {
    const select = event.target
    const expected = select.dataset.expected
    const mismatched = expected && parseFloat(select.value || '0')?.toFixed(2) !== expected
    this.setMismatchState(select, mismatched)
    const lineItem = select.closest('[data-sales-form-target="lineItem"]')
    const quantityInput = lineItem.querySelector('[data-sales-form-target="quantity"]')
    const lineTotalInput = lineItem.querySelector('[data-sales-form-target="lineTotal"]')
    const unitPriceHidden = lineItem.querySelector('input[name*="[unit_price]"]')
    const totalPriceHidden = lineItem.querySelector('input[name*="[total_price]"]')
    const qty = parseInt(quantityInput?.value || '1')
    const price = this.toNumber(select.value)
    const lineTotal = qty * price
    
    console.log('manualUnitPriceSelected: qty=', qty, 'price=', price, 'lineTotal=', lineTotal)
    console.log('  - select element name:', select.name, 'value:', select.value)
    console.log('  - hidden unit price field found:', !!unitPriceHidden)
    console.log('  - hidden total price field found:', !!totalPriceHidden)
    
    // Update hidden field if it exists (for dynamically created items)
    if (unitPriceHidden) {
      unitPriceHidden.value = price.toFixed(2)
      console.log('  - updated hidden unit price to:', unitPriceHidden.value)
    }
    
    // Note: The select element itself (event.target) is the Rails form field and will be submitted
    
    if (lineTotalInput) lineTotalInput.value = lineTotal.toFixed(2)
    if (totalPriceHidden) totalPriceHidden.value = lineTotal.toFixed(2)
    
    this.updateTotals()
  }

  // Select all text when focusing on number inputs for easy replacement
  selectAllOnFocus(event) {
    // Small delay to ensure the field is fully focused before selecting
    setTimeout(() => {
      event.target.select()
    }, 10)
  }

  // Handle custom price input (for admin/manager)
  customPriceChanged(event) {
    const customPriceInput = event.target
    const lineItem = customPriceInput.closest('[data-sales-form-target="lineItem"]')
    const discountReasonRow = lineItem.nextElementSibling
    
    // Only process when input is complete (on 'change' event or when value is empty)
    // This prevents formatting interference while user is still typing
    if (event.type === 'input' && customPriceInput.value !== '') {
      // For input events while typing, just show/hide discount reason row
      if (parseFloat(customPriceInput.value) > 0) {
        if (discountReasonRow && discountReasonRow.dataset.salesFormTarget === 'discountReasonRow') {
          discountReasonRow.classList.remove('hidden')
        }
      } else if (customPriceInput.value === '') {
        if (discountReasonRow && discountReasonRow.dataset.salesFormTarget === 'discountReasonRow') {
          discountReasonRow.classList.add('hidden')
        }
      }
      return // Don't update calculations until change event
    }
    
    // Process calculations on 'change' event or when clearing the field
    if (customPriceInput.value && parseFloat(customPriceInput.value) > 0) {
      // Show discount reason row
      if (discountReasonRow && discountReasonRow.dataset.salesFormTarget === 'discountReasonRow') {
        discountReasonRow.classList.remove('hidden')
      }
      
      // Update price calculation with custom price
      this.updateLineItemWithCustomPrice(lineItem, parseFloat(customPriceInput.value))
    } else {
      // Hide discount reason row
      if (discountReasonRow && discountReasonRow.dataset.salesFormTarget === 'discountReasonRow') {
        discountReasonRow.classList.add('hidden')
      }
      
      // Revert to regular price calculation
      this.updateLineItemPriceForElement(lineItem)
    }
  }

  // Update line item with custom price
  updateLineItemWithCustomPrice(lineItem, customPrice) {
    const quantityInput = lineItem.querySelector('[data-sales-form-target="quantity"]')
    const lineTotalInput = lineItem.querySelector('[data-sales-form-target="lineTotal"]')
    const unitPriceHidden = lineItem.querySelector('input[name*="[unit_price]"]')
    const adjustedPriceHidden = lineItem.querySelector('input[name*="[adjusted_unit_price]"]')
    const adjustedAtHidden = lineItem.querySelector('input[name*="[adjusted_at]"]')
    
    const qty = parseInt(quantityInput?.value || '1')
    const lineTotal = customPrice * qty
    
    // Update display and hidden fields
    if (unitPriceHidden) unitPriceHidden.value = customPrice.toFixed(2)
    if (adjustedPriceHidden) adjustedPriceHidden.value = customPrice.toFixed(2)
    if (adjustedAtHidden) adjustedAtHidden.value = new Date().toISOString()
    if (lineTotalInput) lineTotalInput.value = lineTotal.toFixed(2)
    
    this.updateTotals()
  }

  // Utility to add/remove mismatch highlight and inline hint
  setMismatchState(field, isMismatch) {
    const dangerClasses = ['border-yellow-500', 'bg-yellow-50']
    const normalClasses = ['border-gray-400', 'bg-white']
    if (isMismatch) {
      field.classList.add(...dangerClasses)
      field.classList.remove(...normalClasses)
      // add/help text element below if not exists
      let hint = field.parentNode.querySelector('.triple-check-hint')
      if (!hint) {
        hint = document.createElement('div')
        hint.className = 'triple-check-hint text-xs text-yellow-700 mt-1'
        field.parentNode.appendChild(hint)
      }
      hint.textContent = 'Does not match selected product. Proceeding is allowed; confirm intentionally.'
    } else {
      field.classList.remove(...dangerClasses)
      // keep original styling minimal
      if (!field.readOnly) field.classList.add(...normalClasses)
      const hint = field.parentNode.querySelector('.triple-check-hint')
      if (hint) hint.remove()
    }
  }

  // Get installation preference from sales order type radio buttons
  getSalesOrderInstallationPreference() {
    const productOnlyRadio = this.element.querySelector('input[type="radio"][value="product_only"]')
    const productWithInstallRadio = this.element.querySelector('input[type="radio"][value="product_with_installation"]')
    
    // Check which radio button is selected
    if (productWithInstallRadio && productWithInstallRadio.checked) {
      return true
    }
    if (productOnlyRadio && productOnlyRadio.checked) {
      return false
    }
    
    // Default to installation if neither is explicitly checked (matches model default)
    return true
  }

  // Get appropriate price based on installation selection with fallback logic
  getProductPriceForInstallation(product, includeInstallation) {
    if (!product) return 0
    
    // If installation is requested and product has install price, use it
    // if (includeInstallation && product.standard_install_price && parseFloat(product.standard_install_price) > 0) {
    //   return this.toNumber(product.standard_install_price)
    // }
    
    // If installation is NOT requested, use product-only price
    if (!includeInstallation && product.price) {
      return this.toNumber(product.price)
    }
    
    // Fallback: use whichever price is available
    // if (product.standard_install_price && parseFloat(product.standard_install_price) > 0) {
    //   return this.toNumber(product.standard_install_price)
    // }
    if (product.price) {
      return this.toNumber(product.price)
    }
    
    return 0
  }

  // Calculate product price using API endpoint
  async calculateProductPrice(product, quantity, includeInstallation = false) {
    try {
      const customerType = this.determineCustomerType()
      const installParam = includeInstallation ? '&include_installation=true' : ''

      const response = await fetch(`/api/v1/products/${product.id}/price?quantity=${quantity}&customer_type=${customerType}${installParam}`, {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      return {
        price: data.price,
        warnings: data.warnings || [],
        requires_quote: data.requires_quote,
        within_limits: data.within_limits,
        pricing_tier: data.pricing_tier
      }
    } catch (error) {
      console.error('Error calculating price:', error)
      this.showPricingError(error, product, () => this.calculateProductPrice(product, quantity, includeInstallation))
      // Fallback to local calculation
      return this.calculateProductPriceLocal(product, quantity, includeInstallation)
    }
  }

  // Fallback local price calculation
  calculateProductPriceLocal(product, quantity, includeInstallation = false) {
    const customerType = this.determineCustomerType()
    const pricingTier = this.findPricingTier(product, quantity)

    // Get base price based on installation selection
    let basePrice = this.getProductPriceForInstallation(product, includeInstallation)
    let price = basePrice
    
    // Apply pricing tier adjustments if available
    if (pricingTier) {
      switch (customerType) {
        case 'wholesale':
          price = pricingTier.wholesale_price || pricingTier.base_price || basePrice
          break
        case 'retail':
          price = pricingTier.retail_price || pricingTier.base_price || basePrice
          break
        default:
          price = pricingTier.base_price || basePrice
      }
    }

    return {
      price: price,
      warnings: [],
      requires_quote: product.requires_quote,
      within_limits: this.checkOrderLimits(product, quantity),
      pricing_tier: pricingTier
    }
  }

  // Find the appropriate pricing tier for a product and quantity
  findPricingTier(product, quantity) {
    if (!product.pricing_tiers || product.pricing_tiers.length === 0) {
      return null
    }

    // Find tier that matches the quantity range
    return product.pricing_tiers.find(tier =>
      quantity >= tier.min_quantity && quantity <= tier.max_quantity
    )
  }

  // Determine customer type based on form data or order characteristics
  determineCustomerType() {
    // Simple logic - could be enhanced with customer data
    const totalQuantity = this.calculateTotalQuantity()
    return totalQuantity >= 10 ? 'wholesale' : 'retail'
  }

  // Show product details in the line item
  showProductDetails(lineItem, product) {
    const productDetails = lineItem.querySelector('[data-sales-form-target="productDetails"]')
    const skuElement = lineItem.querySelector('[data-sales-form-target="productSku"]')
    const minQuantityElement = lineItem.querySelector('[data-sales-form-target="minQuantity"]')
    const maxQuantityElement = lineItem.querySelector('[data-sales-form-target="maxQuantity"]')
    const pricingTiers = lineItem.querySelector('[data-sales-form-target="pricingTiers"]')
    const pricingTiersList = lineItem.querySelector('[data-sales-form-target="pricingTiersList"]')

    if (skuElement) skuElement.textContent = product.sku || 'N/A'
    if (minQuantityElement) minQuantityElement.textContent = product.minimum_order_quantity || '1'
    if (maxQuantityElement) maxQuantityElement.textContent = product.maximum_order_quantity || 'No limit'

    // Show pricing tiers if available
    if (product.pricing_tiers && product.pricing_tiers.length > 0 && pricingTiersList) {
      const tiersHtml = product.pricing_tiers.map(tier =>
        `<div>${tier.min_quantity}-${tier.max_quantity}: $${tier.base_price}</div>`
      ).join('')
      pricingTiersList.innerHTML = tiersHtml
      pricingTiers.classList.remove('hidden')
    }

    productDetails.classList.remove('hidden')
  }

  // Clear product details including triple-check fields
  clearLineItemDetails(lineItem) {
    const productDetails = lineItem.querySelector('[data-sales-form-target="productDetails"]')
    const unitPriceInput = lineItem.querySelector('[data-sales-form-target="unitPriceDisplay"]') || lineItem.querySelector('[data-sales-form-target="unitPrice"]')
    const lineTotalInput = lineItem.querySelector('[data-sales-form-target="lineTotal"]')
    const alerts = lineItem.querySelector('[data-sales-form-target="alerts"]')

    // Triple-check system fields (both hidden and display)
    const productCodeInput = lineItem.querySelector('[data-sales-form-target="productCode"]')
    const productNameInput = lineItem.querySelector('[data-sales-form-target="productName"]')
    const productCodeDisplay = lineItem.querySelector('[data-sales-form-target="productCodeDisplay"]')
    const productNameDisplay = lineItem.querySelector('[data-sales-form-target="productNameDisplay"]')

    if (productDetails) productDetails.classList.add('hidden')
    if (unitPriceInput) unitPriceInput.value = ''
    if (lineTotalInput) lineTotalInput.value = ''
    if (alerts) alerts.classList.add('hidden')

    // Clear triple-check fields (both hidden form fields and display fields)
    if (productCodeInput) productCodeInput.value = ''
    if (productNameInput) productNameInput.value = ''
    if (productCodeDisplay) productCodeDisplay.value = ''
    if (productNameDisplay) productNameDisplay.value = ''

    this.updateTotals()
  }

  // Display price warnings and validation messages
  displayPriceWarnings(lineItem, priceData) {
    const alerts = lineItem.querySelector('[data-sales-form-target="alerts"]')
    const quantityInput = lineItem.querySelector('[data-sales-form-target="quantity"]')

    if (!alerts) return

    let alertMessages = []

    // Add warnings from API response
    if (priceData.warnings && priceData.warnings.length > 0) {
      alertMessages = alertMessages.concat(priceData.warnings)
    }

    // Update quantity input styling based on limits
    if (priceData.within_limits === false) {
      quantityInput.classList.add('border-red-500')
    } else {
      quantityInput.classList.remove('border-red-500')
    }

    // Display alerts
    if (alertMessages.length > 0) {
      alerts.innerHTML = alertMessages.map(msg =>
        `<div class="text-yellow-600 text-sm mt-1">⚠️ ${msg}</div>`
      ).join('')
      alerts.classList.remove('hidden')
    } else {
      alerts.classList.add('hidden')
    }
  }

  // Check if quantity is within product order limits (for fallback)
  checkOrderLimits(product, quantity) {
    if (product.minimum_order_quantity && quantity < product.minimum_order_quantity) {
      return false
    }
    if (product.maximum_order_quantity && quantity > product.maximum_order_quantity) {
      return false
    }
    return true
  }

  // Update order totals - calculation logic
  toggleInstallationFields(event) {
    const salesType = event.target.value
    const isProductOnly = salesType === "product_only"
    
    // Toggle installation field visibility
    if (this.hasInstallationFieldTarget) {
      this.installationFieldTarget.classList.toggle("hidden", isProductOnly)
    }
    
    // Toggle installation photos field visibility
    if (this.hasInstallationPhotosFieldTarget) {
      this.installationPhotosFieldTarget.classList.toggle("hidden", isProductOnly)
    }
    
    // Clear installation credit if switching to product-only
    if (isProductOnly && this.hasInstallationCreditTarget) {
      this.installationCreditTarget.value = "0"
    }
    
    // Update all line item prices based on new sales type
    this.updateAllLineItemPrices()
  }

  // Update all line item prices when sales order type changes
  updateAllLineItemPrices() {
    this.lineItemTargets.forEach(lineItem => {
      const productSelect = lineItem.querySelector('[data-sales-form-target="productSelect"]')
      const quantityInput = lineItem.querySelector('[data-sales-form-target="quantity"]')
      
      // Only update if product and quantity are set
      if (productSelect && productSelect.value && quantityInput && quantityInput.value) {
        this.updateLineItemPriceForElement(lineItem)
      }
    })
  }


  updateTotals() {
    const subtotal = this.calculateSubtotal()
    const installationAmount = this.getInstallationCredit()
    const discount = this.getDiscountAmount()
    const shipping = this.getShippingAmount()
    const tax = this.getTaxAmount()

    // Calculation order:
    // 1. Start with subtotal
    // 2. Add installation amount (positive adds charge, negative gives credit)
    // 3. Subtract discount
    // 4. Add shipping
    // 5. Add tax
    const afterInstallation = subtotal + installationAmount
    const afterDiscount = afterInstallation - discount
    const afterShipping = afterDiscount + shipping
    const total = afterShipping + tax

    console.log('updateTotals:', { subtotal, installationAmount, discount, shipping, tax, total })

    // Update display
    if (this.hasSubtotalTarget) {
      this.subtotalTarget.textContent = `$${subtotal.toFixed(2)}`
    }

    if (this.hasTotalTarget) {
      this.totalTarget.textContent = `$${total.toFixed(2)}`
    }

    // Update hidden field for form submission
    if (this.hasTotalAmountFieldTarget) {
      this.totalAmountFieldTarget.value = total.toFixed(2)
      console.log('Updated hidden total field to:', total.toFixed(2))
    }
  }

  // Calculate subtotal from all line items
  calculateSubtotal() {
    let subtotal = 0

    this.lineItemTargets.forEach((lineItem, index) => {
      const destroyField = lineItem.querySelector('[data-sales-form-target="destroyField"]')
      if (destroyField && destroyField.value === "1") return // Skip destroyed items

      const lineTotalInput = lineItem.querySelector('[data-sales-form-target="lineTotal"]')
      if (lineTotalInput && lineTotalInput.value) {
        const lineTotal = parseFloat(lineTotalInput.value)
        console.log(`Line item ${index + 1}: lineTotal field value = ${lineTotalInput.value}, parsed = ${lineTotal}`)
        subtotal += lineTotal
      } else {
        console.log(`Line item ${index + 1}: lineTotal field not found or empty`, lineTotalInput)
      }
    })

    console.log('calculateSubtotal result:', subtotal)
    return subtotal
  }

  // Calculate total quantity across all line items
  calculateTotalQuantity() {
    let totalQty = 0

    this.lineItemTargets.forEach(lineItem => {
      const destroyField = lineItem.querySelector('[data-sales-form-target="destroyField"]')
      if (destroyField && destroyField.value === "1") return // Skip destroyed items

      const quantityInput = lineItem.querySelector('[data-sales-form-target="quantity"]')
      if (quantityInput && quantityInput.value) {
        totalQty += parseInt(quantityInput.value)
      }
    })

    return totalQty
  }

  // Get discount amount
  getDiscountAmount() {
    if (this.hasDiscountAmountTarget && this.discountAmountTarget.value) {
      return parseFloat(this.discountAmountTarget.value)
    }
    return 0
  }

  // Get tax amount
  getTaxAmount() {
    if (this.hasTaxAmountTarget && this.taxAmountTarget.value) {
      return parseFloat(this.taxAmountTarget.value)
    }
    return 0
  }

  // Get installation credit amount (this is a deduction)
  getInstallationCredit() {
    if (this.hasInstallationCreditTarget && this.installationCreditTarget.value) {
      return parseFloat(this.installationCreditTarget.value)
    }
    return 0
  }

  // Get shipping amount
  getShippingAmount() {
    if (this.hasShippingAmountTarget && this.shippingAmountTarget.value) {
      return parseFloat(this.shippingAmountTarget.value)
    }
    return 0
  }

  // Save draft functionality
  async saveDraft() {
    // Clear any validation errors before saving draft
    this.clearAllErrors()

    this.showLoading()

    try {
      const formData = new FormData(this.element)
      formData.append('sales_order[status]', 'draft')

      // Determine if this is a new or existing order
      const isEdit = this.element.action.includes('/sales_orders/') && !this.element.action.endsWith('/sales_orders')
      const url = isEdit ? this.element.action : '/sales_orders/save_draft'
      const method = isEdit ? 'PATCH' : 'POST'

      const response = await fetch(url, {
        method: method,
        body: formData,
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      const result = await response.json()

      if (result.success) {
        this.showNotification('Draft saved successfully!', 'success')

        // Update form action if this was a new record
        if (result.sales_order_id && !this.element.action.includes(result.sales_order_id)) {
          this.element.action = this.element.action.replace('/sales_orders', `/sales_orders/${result.sales_order_id}`)
          this.element.method = 'patch'

          // Add hidden method field for Rails
          let methodField = this.element.querySelector('input[name="_method"]')
          if (!methodField) {
            methodField = document.createElement('input')
            methodField.type = 'hidden'
            methodField.name = '_method'
            methodField.value = 'patch'
            this.element.appendChild(methodField)
          }
        }

        // Update page title if available
        if (result.sales_order_id) {
          document.title = document.title.replace('Create Sales Order', `Edit Sales Order #${result.sales_order_id}`)
        }

      } else {
        this.showNotification(result.message || 'Failed to save draft', 'error')
        if (result.errors && result.errors.length > 0) {
          console.error('Draft save errors:', result.errors)
        }
      }
    } catch (error) {
      console.error('Save draft error:', error)
      this.showDataError({
        operation: 'saving draft',
        technical: error.message
      }, () => this.saveDraft())
    } finally {
      this.hideLoading()
    }
  }

  // Check if form has unsaved changes
  hasUnsavedChanges() {
    // Simple check - in a real app you might want more sophisticated change detection
    const customerName = this.hasCustomerNameTarget ? this.customerNameTarget.value.trim() : ''
    const customerEmail = this.hasCustomerEmailTarget ? this.customerEmailTarget.value.trim() : ''
    const hasLineItems = this.lineItemTargets.some(item => {
      const productSelect = item.querySelector('[data-sales-form-target="productSelect"]')
      const quantity = item.querySelector('[data-sales-form-target="quantity"]')
      return productSelect && productSelect.value && quantity && quantity.value
    })

    return customerName || customerEmail || hasLineItems
  }

  // Before page unload warning
  beforeUnload(event) {
    if (this.hasUnsavedChanges()) {
      event.preventDefault()
      event.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
      return event.returnValue
    }
  }

  // Show loading overlay
  showLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove('hidden')
    }
  }

  // Hide loading overlay
  hideLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add('hidden')
    }
  }

  // Show notification message (legacy - being replaced by error notification system)
  showNotification(message, type = 'info') {
    // Create a simple notification - could be enhanced with a proper notification system
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 p-4 rounded-md shadow-lg z-50 ${type === 'success' ? 'bg-green-500 text-white' :
      type === 'error' ? 'bg-red-500 text-white' :
        'bg-blue-500 text-white'
      }`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 3000)
  }

  // Contextual error handling methods using new error notification system
  showPricingError(error, product, retryCallback) {
    this.getErrorNotificationController()?.showDataError({
      operation: 'calculating pricing',
      technical: `Failed to calculate price for ${product.name}: ${error.message}`,
      product: product.name
    }, retryCallback)
  }

  showDataError(context, retryCallback) {
    this.getErrorNotificationController()?.showDataError(context, retryCallback)
  }

  showConnectionError(context, retryCallback) {
    this.getErrorNotificationController()?.showConnectionError(context, retryCallback)
  }

  showValidationError(errors, context = {}) {
    this.getErrorNotificationController()?.showValidationError(errors, {
      operation: 'form validation',
      ...context
    })
  }

  // Get or create error notification controller instance
  getErrorNotificationController() {
    if (!this.errorNotificationController) {
      // Try to find existing controller
      const existingController = this.application.getControllerForElementAndIdentifier(
        document.body, 'error-notification'
      )
      
      if (existingController) {
        this.errorNotificationController = existingController
      } else {
        // Create error notification controller if it doesn't exist
        document.body.setAttribute('data-controller', 
          (document.body.getAttribute('data-controller') || '') + ' error-notification'
        )
        this.errorNotificationController = this.application.getControllerForElementAndIdentifier(
          document.body, 'error-notification'
        )
      }
    }
    
    return this.errorNotificationController
  }

  // Form validation methods
  validateCustomerName() {
    const value = this.customerNameTarget.value.trim()
    const fieldName = 'customer_name'

    if (!value) {
      this.setFieldError(this.customerNameTarget, 'Customer name is required')
      return false
    }

    if (value.length < 2) {
      this.setFieldError(this.customerNameTarget, 'Customer name must be at least 2 characters')
      return false
    }

    this.clearFieldError(this.customerNameTarget)
    return true
  }

  validateCustomerEmail() {
    const value = this.customerEmailTarget.value.trim()
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!value) {
      this.setFieldError(this.customerEmailTarget, 'Email is required')
      return false
    }

    if (!emailRegex.test(value)) {
      this.setFieldError(this.customerEmailTarget, 'Please enter a valid email address')
      return false
    }

    this.clearFieldError(this.customerEmailTarget)
    return true
  }

  validateCustomerPhone() {
    const value = this.customerPhoneTarget.value.trim()
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/

    // Phone is optional, so empty is valid
    if (!value) {
      this.clearFieldError(this.customerPhoneTarget)
      return true
    }

    if (!phoneRegex.test(value.replace(/[\s\-\(\)]/g, ''))) {
      this.setFieldError(this.customerPhoneTarget, 'Please enter a valid phone number')
      return false
    }

    this.clearFieldError(this.customerPhoneTarget)
    return true
  }

  validateDiscountAmount() {
    const value = parseFloat(this.discountAmountTarget.value) || 0
    const subtotal = this.calculateSubtotal()

    if (value < 0) {
      this.setFieldError(this.discountAmountTarget, 'Discount amount cannot be negative')
      return false
    }

    if (value > subtotal) {
      this.setFieldError(this.discountAmountTarget, 'Discount cannot exceed subtotal')
      return false
    }

    this.clearFieldError(this.discountAmountTarget)
    return true
  }

  validateTaxAmount() {
    const value = parseFloat(this.taxAmountTarget.value) || 0

    if (value < 0) {
      this.setFieldError(this.taxAmountTarget, 'Tax amount cannot be negative')
      return false
    }

    this.clearFieldError(this.taxAmountTarget)
    return true
  }

  validateLineItems() {
    let hasValidItems = false
    let allItemsValid = true

    this.lineItemTargets.forEach(lineItem => {
      const destroyField = lineItem.querySelector('[data-sales-form-target="destroyField"]')
      if (destroyField && destroyField.value === "1") return // Skip destroyed items

      const productSelect = lineItem.querySelector('[data-sales-form-target="productSelect"]')
      const quantityInput = lineItem.querySelector('[data-sales-form-target="quantity"]')

      if (productSelect && productSelect.value && quantityInput && quantityInput.value) {
        hasValidItems = true

        // Validate individual line item
        if (!this.validateLineItem(lineItem)) {
          allItemsValid = false
        }
      }
    })

    if (!hasValidItems) {
      this.showValidationError('At least one product must be selected')
      return false
    }

    return allItemsValid
  }

  validateLineItem(lineItem) {
    const productSelect = lineItem.querySelector('[data-sales-form-target="productSelect"]')
    const quantityInput = lineItem.querySelector('[data-sales-form-target="quantity"]')
    const unitPriceInput = lineItem.querySelector('[data-sales-form-target="unitPriceDisplay"]') || lineItem.querySelector('[data-sales-form-target="unitPrice"]')

    if (!productSelect.value) {
      this.setFieldError(productSelect, 'Please select a product')
      return false
    }

    const quantity = parseInt(quantityInput.value)
    if (!quantity || quantity < 1) {
      this.setFieldError(quantityInput, 'Quantity must be at least 1')
      return false
    }

    const unitPrice = parseFloat(unitPriceInput.value)
    if (!unitPrice || unitPrice < 0) {
      this.setFieldError(unitPriceInput, 'Invalid unit price')
      return false
    }

    return true
  }

  // Form submission validation
  handleFormSubmit(event) {
    // Don't validate for draft saves
    if (event.submitter && event.submitter.dataset.salesFormTarget === 'saveDraftBtn') {
      return true
    }

    let isValid = true

    // Clear previous validation errors
    this.clearAllErrors()

    // Validate customer information
    if (!this.validateCustomerName()) isValid = false
    if (!this.validateCustomerEmail()) isValid = false
    if (!this.validateCustomerPhone()) isValid = false

    // Validate amounts
    if (!this.validateDiscountAmount()) isValid = false
    if (!this.validateTaxAmount()) isValid = false

    // Validate line items
    if (!this.validateLineItems()) isValid = false

    // Validate totals
    if (!this.validateTotals()) isValid = false

    if (!isValid) {
      event.preventDefault()
      this.showValidationError('Please fix the errors above and try again')
      return false
    }

    // Show confirmation for final submission
    if (event.submitter && event.submitter.dataset.salesFormTarget === 'submitBtn') {
      const total = this.totalTarget.textContent
      const lineItemCount = this.lineItemTargets.filter(item => {
        const destroyField = item.querySelector('[data-sales-form-target="destroyField"]')
        return !(destroyField && destroyField.value === "1")
      }).length
    }

    return true
  }

  validateTotals() {
    const total = parseFloat(this.totalTarget.textContent.replace('$', ''))

    if (isNaN(total) || total < 0) {
      this.showValidationError('Order total is invalid')
      return false
    }

    return true
  }

  // Error handling methods
  setFieldError(field, message) {
    // Add error styling
    field.classList.add('border-red-500', 'ring-red-500')
    field.classList.remove('border-gray-300')

    // Find or create error message element
    let errorElement = field.parentNode.querySelector('.field-error')
    if (!errorElement) {
      errorElement = document.createElement('div')
      errorElement.className = 'field-error text-red-600 text-sm mt-1'
      field.parentNode.appendChild(errorElement)
    }

    errorElement.textContent = message

    // Store error in validation map
    this.validationErrors.set(field, message)
  }

  clearFieldError(field) {
    // Remove error styling
    field.classList.remove('border-red-500', 'ring-red-500')
    field.classList.add('border-gray-300')

    // Remove error message
    const errorElement = field.parentNode.querySelector('.field-error')
    if (errorElement) {
      errorElement.remove()
    }

    // Remove from validation map
    this.validationErrors.delete(field)
  }

  clearAllErrors() {
    // Clear all field errors
    this.validationErrors.forEach((message, field) => {
      this.clearFieldError(field)
    })
    this.validationErrors.clear()

    // Clear general validation error
    this.hideValidationError()
  }

  hideValidationError() {
    const errorElement = document.querySelector('.form-validation-error')
    if (errorElement) {
      errorElement.remove()
    }
  }

  // Create line item template
  createLineItemTemplate() {
    const index = this.lineItemIndex++

    // Match view layout and triple-check system (single source of truth: product select)
    return `
      <div class="flex items-center space-x-2 py-2 border-b border-gray-200" data-sales-form-target="lineItem">
        <!-- Hidden fields for Rails form -->
        <input type="hidden" name="sales_order[line_items_attributes][${index}][_destroy]" value="0" data-sales-form-target="destroyField">
        <input type="hidden" name="sales_order[line_items_attributes][${index}][product_code]" data-sales-form-target="productCode">
        <input type="hidden" name="sales_order[line_items_attributes][${index}][product_name]" data-sales-form-target="productName">

        <!-- Quantity -->
        <div class="w-16">
          <input type="number"
                 name="sales_order[line_items_attributes][${index}][quantity]"
                 value="1"
                 min="1"
                 class="w-full px-2 py-1 text-sm border-0 border-b-2 border-gray-400 bg-transparent focus:border-blue-500 focus:ring-0 text-center"
                 data-sales-form-target="quantity"
                 data-action="input->sales-form#updateLineItemPrice change->sales-form#updateLineItemPrice blur->sales-form#updateLineItemPrice">
        </div>

        <!-- Product Selection (Single source of truth) -->
        <div class="flex-1 min-w-0">
          <select name="sales_order[line_items_attributes][${index}][product_id]"
                  class="w-full px-2 py-1 text-sm border-0 border-b-2 border-gray-400 bg-white focus:border-blue-500 focus:ring-0"
                  data-sales-form-target="productSelect"
                  data-action="change->sales-form#updateLineItemPrice">
            <option value="">Select Product...</option>
            ${this.productsValue.map(product =>
      `<option value="${product.id}">${product.name}</option>`
    ).join('')}
          </select>
        </div>

        <!-- Product Code (select to confirm) -->
        <div class="w-32">
          <select class="w-full px-2 py-1 text-sm border-0 border-b-2 border-gray-400 bg-white text-gray-800 text-center"
                  data-sales-form-target="productCodeDisplay"
                  data-action="change->sales-form#manualProductCodeSelected">
            <option value="">Pick Code…</option>
          </select>
        </div>


        <!-- Unit Price (select to confirm) -->
        <div class="w-28">
          <select class="w-full px-2 py-1 text-sm border-0 border-b-2 border-gray-400 bg-white text-gray-800 text-right"
                  data-sales-form-target="unitPriceDisplay"
                  data-action="change->sales-form#manualUnitPriceSelected">
            <option value="">Pick Price…</option>
          </select>
          <input type="hidden" name="sales_order[line_items_attributes][${index}][unit_price]" value="">
        </div>

        <!-- Remove -->
        <div class="w-8">
          <button type="button" class="w-8 h-8 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-full flex items-center justify-center transition-colors"
                  data-action="click->sales-form#removeLineItem" title="Remove this item">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Hidden total for calculations -->
        <input type="hidden" name="sales_order[line_items_attributes][${index}][total_price]" data-sales-form-target="lineTotal">

        <!-- Alerts (populated dynamically) -->
        <div class="hidden col-span-6 mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md" data-sales-form-target="alerts"></div>

        <!-- Product details (populated dynamically) -->
        <div class="hidden col-span-6 mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md" data-sales-form-target="productDetails">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span class="font-medium text-gray-700">SKU:</span>
              <span class="ml-2 text-gray-600" data-sales-form-target="productSku"></span>
            </div>
            <div>
              <span class="font-medium text-gray-700">Min Qty:</span>
              <span class="ml-2 text-gray-600" data-sales-form-target="minQuantity"></span>
            </div>
            <div>
              <span class="font-medium text-gray-700">Max Qty:</span>
              <span class="ml-2 text-gray-600" data-sales-form-target="maxQuantity"></span>
            </div>
          </div>
          <div class="mt-2 hidden" data-sales-form-target="pricingTiers">
            <div class="font-medium text-gray-700 mb-2">Pricing Tiers:</div>
            <div class="space-y-1" data-sales-form-target="pricingTiersList"></div>
          </div>
        </div>
      </div>
    `
  }
}